import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAdmin } from "@/lib/adminContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  STATUSES,
  DEPARTMENTS,
  STATUS_STYLES,
  PRIORITY_STYLES,
} from "@/lib/constants";
import { formatAge, isUrgentOpen } from "@/lib/ticketUtils";
import {
  Loader2,
  Clock,
  Flame,
  CirclePlus,
  Building,
  ArrowRightLeft,
  StickyNote,
  CheckCircle2,
  Activity,
} from "lucide-react";
import { toast } from "sonner";

function fmtDate(s) {
  if (!s) return "—";
  return new Date(s).toLocaleString();
}

const EVENT_META = {
  created: { icon: CirclePlus, color: "text-[color:var(--enka-blue)]", bg: "bg-[color:var(--enka-blue-50)]" },
  department_assigned: { icon: Building, color: "text-slate-700", bg: "bg-slate-100" },
  department_changed: { icon: ArrowRightLeft, color: "text-slate-700", bg: "bg-slate-100" },
  status_changed: { icon: Activity, color: "text-amber-700", bg: "bg-amber-50" },
  note_updated: { icon: StickyNote, color: "text-[color:var(--enka-blue)]", bg: "bg-[color:var(--enka-blue-50)]" },
  closed: { icon: CheckCircle2, color: "text-emerald-700", bg: "bg-emerald-50" },
};

function eventLabel(ev) {
  switch (ev.type) {
    case "created":
      return ev.by ? `Ticket submitted by ${ev.by}` : "Ticket submitted";
    case "department_assigned":
      return `Assigned to ${ev.detail || "—"}`;
    case "department_changed":
      return `Department changed: ${ev.from || "—"} → ${ev.to || "—"}`;
    case "status_changed":
      return `Status changed: ${ev.from || "—"} → ${ev.to || "—"}`;
    case "note_updated":
      return ev.detail || "Admin note updated";
    case "closed":
      return "Ticket closed";
    default:
      return ev.type;
  }
}

function Timeline({ events }) {
  if (!events || events.length === 0) {
    return <div className="text-sm text-[color:var(--enka-muted)]">No activity yet.</div>;
  }
  // Show newest first
  const sorted = [...events].sort((a, b) => new Date(b.at) - new Date(a.at));
  return (
    <ol className="space-y-3" data-testid="detail-timeline">
      {sorted.map((ev, idx) => {
        const meta = EVENT_META[ev.type] || { icon: Activity, color: "text-slate-700", bg: "bg-slate-100" };
        const Icon = meta.icon;
        return (
          <li key={idx} className="flex gap-3" data-testid={`timeline-event-${ev.type}`}>
            <div className={`h-7 w-7 shrink-0 rounded-full ${meta.bg} ${meta.color} flex items-center justify-center mt-0.5`}>
              <Icon className="h-3.5 w-3.5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm text-[color:var(--enka-ink)] leading-snug">{eventLabel(ev)}</div>
              <div className="text-[11px] text-[color:var(--enka-muted)] mt-0.5">
                {fmtDate(ev.at)}
                {ev.by ? ` · by ${ev.by}` : ""}
              </div>
            </div>
          </li>
        );
      })}
    </ol>
  );
}

export default function TicketDetailDialog({ ticketId, onClose, onUpdated }) {
  const { admin } = useAdmin();
  const isSuper = admin?.role === "super";
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [saving, setSaving] = useState(false);
  const [closing, setClosing] = useState(false);
  const [form, setForm] = useState({ status: "", department: "", admin_note: "" });

  useEffect(() => {
    if (!ticketId) {
      setTicket(null);
      setNotFound(false);
      return;
    }
    setLoading(true);
    setNotFound(false);
    api
      .get(`/admin/tickets/${ticketId}`)
      .then((r) => {
        setTicket(r.data);
        setForm({
          status: r.data.status,
          department: r.data.department,
          admin_note: r.data.admin_note || "",
        });
      })
      .catch((e) => {
        if (e?.response?.status === 404) {
          setNotFound(true);
        } else {
          toast.error("Failed to load ticket");
        }
      })
      .finally(() => setLoading(false));
  }, [ticketId]);

  const hasChanges =
    ticket &&
    (form.status !== ticket.status ||
      form.department !== ticket.department ||
      form.admin_note !== (ticket.admin_note || ""));

  const save = async () => {
    if (!ticket) return;
    setSaving(true);
    try {
      const body = {};
      if (form.status !== ticket.status) body.status = form.status;
      if (form.department !== ticket.department) body.department = form.department;
      if (form.admin_note !== (ticket.admin_note || "")) body.admin_note = form.admin_note;
      if (Object.keys(body).length === 0) {
        toast.info("No changes to save");
        setSaving(false);
        return;
      }
      const { data } = await api.patch(`/admin/tickets/${ticket.id}`, body);
      setTicket(data);
      setForm({ status: data.status, department: data.department, admin_note: data.admin_note || "" });
      onUpdated?.(data);
      toast.success("Ticket updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    } finally {
      setSaving(false);
    }
  };

  const closeTicket = async () => {
    if (!ticket) return;
    setClosing(true);
    try {
      const { data } = await api.patch(`/admin/tickets/${ticket.id}`, { status: "Closed" });
      setTicket(data);
      setForm((s) => ({ ...s, status: "Closed" }));
      onUpdated?.(data);
      toast.success("Ticket closed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to close ticket");
    } finally {
      setClosing(false);
    }
  };

  const open = Boolean(ticketId);
  const urgent = ticket && isUrgentOpen(ticket);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent
        className="max-w-3xl max-h-[92vh] overflow-y-auto"
        data-testid="ticket-detail-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-3">
            <span className="font-mono text-[color:var(--enka-blue)] text-xl">
              {ticket?.ticket_no || "Ticket"}
            </span>
            {ticket && (
              <>
                <span className={`enka-pill ${PRIORITY_STYLES[ticket.priority]}`}>
                  {urgent && <Flame className="h-3 w-3" />}
                  {ticket.priority}
                </span>
                <span className={`enka-pill ${STATUS_STYLES[ticket.status]}`}>
                  {ticket.status}
                </span>
                <span
                  className="inline-flex items-center gap-1 text-xs text-[color:var(--enka-muted)]"
                  data-testid="detail-age"
                >
                  <Clock className="h-3 w-3" /> Open for {formatAge(ticket.created_at)}
                </span>
              </>
            )}
          </DialogTitle>
          <DialogDescription>Ticket details, activity timeline and admin actions.</DialogDescription>
        </DialogHeader>

        {loading || !ticket ? (
          notFound ? (
            <div className="py-10 text-center" data-testid="detail-not-found">
              <div className="text-sm text-[color:var(--enka-muted)]">
                This ticket is outside your scope or no longer exists.
              </div>
              <Button
                type="button"
                onClick={onClose}
                className="mt-4 bg-[color:var(--enka-blue)] hover:bg-[color:var(--enka-blue-600)] text-white"
              >
                Close
              </Button>
            </div>
          ) : (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-[color:var(--enka-blue)]" />
            </div>
          )
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            {/* Left: details + form (2 cols) */}
            <div className="lg:col-span-2 space-y-5">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <Field label="Area" value={ticket.area} />
                <Field label="Category" value={ticket.category} />
                <Field
                  label="Name / Username"
                  value={
                    <span className="flex items-center gap-2 flex-wrap">
                      <span>{ticket.name}</span>
                      {ticket.user_id ? (
                        <span
                          className="enka-pill bg-blue-50 text-blue-800 border border-blue-200"
                          data-testid="detail-registered-badge"
                        >
                          Registered · @{ticket.user_username}
                        </span>
                      ) : (
                        <span
                          className="enka-pill bg-slate-100 text-slate-700 border border-slate-200"
                          data-testid="detail-guest-badge"
                        >
                          Guest
                        </span>
                      )}
                    </span>
                  }
                />
                <Field label="Location" value={ticket.location} />
                {ticket.area === "Camp" && (
                  <>
                    <Field label="Block" value={ticket.block} />
                    <Field label="Room" value={ticket.room} />
                  </>
                )}
                {ticket.area === "Site" && (
                  <>
                    <Field label="Office" value={ticket.office} />
                    <Field label="Requester Department" value={ticket.requester_department} />
                  </>
                )}
                <Field label="Created" value={fmtDate(ticket.created_at)} />
                <Field label="Last Updated" value={fmtDate(ticket.updated_at)} />
              </div>

              <div>
                <Label className="text-xs uppercase tracking-wide text-[color:var(--enka-muted)]">
                  Description
                </Label>
                <div className="mt-1 rounded-md border border-border bg-[#fafbfd] p-3 text-sm text-[color:var(--enka-ink)] whitespace-pre-wrap">
                  {ticket.description}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2 border-t border-border">
                <div>
                  <Label className="text-xs">Status</Label>
                  <Select value={form.status} onValueChange={(v) => setForm((s) => ({ ...s, status: v }))}>
                    <SelectTrigger data-testid="detail-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">
                    Assigned Department
                    {!isSuper && (
                      <span className="ml-2 text-[10px] uppercase tracking-wider text-[color:var(--enka-muted)]">
                        (Super Admin only)
                      </span>
                    )}
                  </Label>
                  <Select
                    value={form.department}
                    onValueChange={(v) => setForm((s) => ({ ...s, department: v }))}
                    disabled={!isSuper}
                  >
                    <SelectTrigger
                      data-testid="detail-department-select"
                      data-disabled={!isSuper ? "true" : "false"}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DEPARTMENTS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label className="text-xs">Admin Note</Label>
                <Textarea
                  rows={3}
                  value={form.admin_note}
                  onChange={(e) => setForm((s) => ({ ...s, admin_note: e.target.value }))}
                  placeholder="Internal notes for this ticket..."
                  data-testid="detail-admin-note"
                />
              </div>
            </div>

            {/* Right: timeline */}
            <div className="lg:col-span-1">
              <div className="rounded-lg border border-border bg-[#fafbfd] p-4 h-full">
                <div className="flex items-center gap-2 mb-3">
                  <Activity className="h-4 w-4 text-[color:var(--enka-blue)]" />
                  <h3 className="font-display text-sm text-[color:var(--enka-ink)]">Activity timeline</h3>
                </div>
                <Timeline events={ticket.events} />
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            data-testid="detail-cancel-btn"
          >
            Close
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={closeTicket}
            disabled={closing || saving || !ticket || ticket.status === "Closed"}
            data-testid="detail-close-ticket-btn"
          >
            {closing ? <Loader2 className="h-4 w-4 animate-spin" /> : "Close Ticket"}
          </Button>
          <Button
            type="button"
            onClick={save}
            disabled={saving || !ticket || !hasChanges}
            className="bg-[color:var(--enka-blue)] hover:bg-[color:var(--enka-blue-600)] text-white"
            data-testid="detail-save-btn"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-xs text-[color:var(--enka-muted)] uppercase tracking-wide">{label}</div>
      <div className="mt-1 text-[color:var(--enka-ink)]">{value || "—"}</div>
    </div>
  );
}
